import { Link } from '@tanstack/react-router';
import { formatInTimeZone } from 'date-fns-tz';

import { formatTimeRange } from '@apps/web/components/caregivers/caregiver_availability_schedule_model';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Spinner } from '@apps/web/components/ui/spinner';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';

export type ShiftDetails = {
    readonly id: string;
    readonly patient_name: string;
    readonly shift_start_time: string;
    readonly shift_end_time: string;
    readonly status: string;
    readonly role_required?: string | null;
};

function formatShiftStatus(status: string): string {
    const label = status.split('_').join(' ');
    return label.charAt(0).toUpperCase() + label.slice(1);
}

/**
 * Minimal shift popup for calendar shift chips and chat shift references:
 * who, when, and status. The shifts page remains the full detail surface.
 *
 * Callers that fetch the shift on demand (chat) drive `open` themselves and
 * pass `isLoading`/`loadFailed` so the popup opens immediately on click
 * instead of appearing to do nothing while the request is in flight.
 */
export function CaregiverShiftDetailsDialog({
    shift,
    timezone,
    onClose,
    open = shift !== null,
    isLoading = false,
    loadFailed = false,
    onRetry,
    viewShiftInNewTab = false,
}: {
    readonly shift: ShiftDetails | null;
    readonly timezone: string;
    readonly onClose: () => void;
    readonly open?: boolean;
    readonly isLoading?: boolean;
    readonly loadFailed?: boolean;
    readonly onRetry?: () => void;
    // The extension panel cancels in-app navigation, so its "View shift"
    // action must open the web app in a new tab instead.
    readonly viewShiftInNewTab?: boolean;
}) {
    const { formatName } = useFormatPersonName();
    return (
        <Dialog
            open={open}
            onOpenChange={(dialogOpen) => {
                if (!dialogOpen) {
                    onClose();
                }
            }}
        >
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Shift details</DialogTitle>
                    <DialogDescription>
                        {shift
                            ? formatInTimeZone(
                                  shift.shift_start_time,
                                  timezone,
                                  'EEE, MMM d',
                              )
                            : null}
                    </DialogDescription>
                </DialogHeader>
                {shift ? (
                    <dl className="space-y-2 text-sm">
                        <div className="flex items-baseline justify-between gap-2">
                            <dt className="text-muted-foreground">Client</dt>
                            <dd className="text-right font-medium">
                                {formatName(shift.patient_name)}
                            </dd>
                        </div>
                        <div className="flex items-baseline justify-between gap-2">
                            <dt className="text-muted-foreground">Time</dt>
                            <dd className="text-right tabular-nums">
                                {formatTimeRange(
                                    shift.shift_start_time,
                                    shift.shift_end_time,
                                    timezone,
                                )}
                            </dd>
                        </div>
                        {shift.role_required ? (
                            <div className="flex items-baseline justify-between gap-2">
                                <dt className="text-muted-foreground">Role</dt>
                                <dd className="text-right">
                                    {shift.role_required.toUpperCase()}
                                </dd>
                            </div>
                        ) : null}
                        <div className="flex items-baseline justify-between gap-2">
                            <dt className="text-muted-foreground">Status</dt>
                            <dd className="text-right">
                                <Badge variant="secondary">
                                    {formatShiftStatus(shift.status)}
                                </Badge>
                            </dd>
                        </div>
                    </dl>
                ) : isLoading ? (
                    <div className="flex items-center justify-center py-4">
                        <Spinner size="lg" tone="muted" />
                    </div>
                ) : loadFailed ? (
                    <p className="text-muted-foreground text-sm">
                        Couldn’t load this shift.
                    </p>
                ) : null}
                {shift ? (
                    <DialogFooter>
                        <Button
                            asChild={true}
                            size="sm"
                            className="h-7 px-2.5 text-xs"
                        >
                            {viewShiftInNewTab ? (
                                <a
                                    href={new URL(
                                        `/shifts/${shift.id}`,
                                        window.location.origin,
                                    ).toString()}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    View shift
                                </a>
                            ) : (
                                <Link
                                    to="/shifts/$shift-id"
                                    params={{ 'shift-id': shift.id }}
                                >
                                    View shift
                                </Link>
                            )}
                        </Button>
                    </DialogFooter>
                ) : loadFailed && onRetry ? (
                    <DialogFooter>
                        <Button
                            size="sm"
                            className="h-7 px-2.5 text-xs"
                            onClick={onRetry}
                        >
                            Try again
                        </Button>
                    </DialogFooter>
                ) : null}
            </DialogContent>
        </Dialog>
    );
}
